import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarDays, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { fetchWeeklySummary, formatUsd } from '@/lib/timeEntriesData';
import { translateRole } from '@/lib/i18nLabels';

export const WeeklySummary = ({ refreshKey = 0 }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ rows: [], fromDate: '', toDate: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchWeeklySummary()
      .then((d) => alive && setData(d))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const max = Math.max(...data.rows.map((r) => r.hours), 1);

  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="weekly-summary"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarDays size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('crewClock.weekly_summary')}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {data.fromDate} → {data.toDate}
          </div>
        </div>
        {loading ? (
          <div className="mt-4 flex items-center justify-center text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('crewClock.loading')}
          </div>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.rows.map(({ crew, hours, cost, entries }) => {
              const pct = (hours / max) * 100;
              return (
                <li
                  key={crew.id}
                  className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3 py-2.5"
                  data-testid={`weekly-row-${crew.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 border border-[hsl(var(--primary))]/30">
                      <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-xs font-semibold">
                        {crew.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {crew.name}
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        {translateRole(t, crew.role)} ·{' '}
                        {t('crewClock.entries.count_short', { count: entries })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-foreground">
                        {hours.toFixed(2)}h
                      </div>
                      <div className="text-xs text-[hsl(var(--success))]">
                        {formatUsd(cost)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklySummary;
